import { DragDropModule, DragConstrainPosition } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { filter, auditTime } from 'rxjs';
import { CustomerConfigService } from '../../../core/customer/customer-config.service';
import { EventHealthSaveNotifications } from '../../../core/services/CTEs/event-health-save.interceptor';
import { normalizeHealthUid } from './event-health-targets';
import { EventHealthService } from '../../../core/services/CTEs/event-health.service';
import { healthAssessmentNotice, healthFindingExplanation, mapHealthReview } from './event-health-form-review.mapper';
import { EventHealthFormBridgeService, EventHealthFormReviewService } from './event-health-form-review.service';
import {
  HealthReviewFinding,
  HealthReviewSuggestion,
} from './event-health-form-review.types';

@Component({
  selector: 'app-event-health-form-review',
  standalone: true,
  imports: [DragDropModule, CommonModule, MatIconModule, MatSnackBarModule],
  templateUrl: './event-health-form-review.component.html',
  styleUrls: ['./event-health-form-review.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventHealthFormReviewComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly bridge = inject(EventHealthFormBridgeService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly eventHealthService = inject(EventHealthService);
  private readonly customerConfig = inject(CustomerConfigService);
  private readonly saves = inject(EventHealthSaveNotifications);
  private loadSequence = 0;

  readonly session = inject(EventHealthFormReviewService);
  readonly collapsed = signal(false);
  readonly constrainDrag: DragConstrainPosition = (point, _drag, dimensions, pickup) => ({
    x: Math.max(0, Math.min(point.x - pickup.x, window.innerWidth - dimensions.width)),
    y: Math.max(0, Math.min(point.y - pickup.y, window.innerHeight - dimensions.height)),
  });
  readonly findings = this.session.findings;
  readonly context = this.session.context;
  readonly assessment = this.session.assessment;
  readonly errorExplanation = healthFindingExplanation;
  readonly assessmentNotice = computed(() => healthAssessmentNotice(this.context()));
  readonly progress = computed(() => {
    const total = this.findings().length;
    return total ? Math.round((this.session.completedCount() / total) * 100)
      : this.context()?.status === 'ready' && !this.assessmentNotice() ? 100 : 0;
  });

  ngOnInit(): void {
    this.syncFromUrl(true);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.syncFromUrl(true));
    this.saves.saved.pipe(
      filter(uid => normalizeHealthUid(uid) === normalizeHealthUid(this.context()?.eventUid)),
      // Let existing save handlers update their forms before replacing the assessment.
      auditTime(0), takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.syncFromUrl());
  }

  toggleCollapsed(): void {
    this.collapsed.update((value) => !value);
  }

  selectFinding(finding: HealthReviewFinding): void {
    this.collapsed.set(false);
    this.bridge.focusFinding(finding);
  }

  applySuggestion(finding: HealthReviewFinding, suggestion: HealthReviewSuggestion, event: MouseEvent): void {
    event.stopPropagation();

    if (this.bridge.applySuggestion(finding, suggestion)) {
      this.snackBar.open(`${finding.target.label} updated.`, '', { duration: 2600 });
      return;
    }

    this.bridge.focusFinding(finding);
    this.snackBar.open(`Open ${finding.target.label} and choose “${suggestion.label}”.`, '', { duration: 3200 });
  }

  restoreOriginalValue(finding: HealthReviewFinding, event: MouseEvent): void {
    event.stopPropagation();

    if (this.bridge.restoreOriginalValue(finding)) {
      this.snackBar.open(`${finding.target.label} restored to its original value.`, '', { duration: 2600 });
      return;
    }

    this.snackBar.open(`The original value for ${finding.target.label} could not be restored.`, '', { duration: 3200 });
  }

  nextFinding(): void {
    const findings = this.findings();
    if (!findings.length) return;

    const index = findings.findIndex((finding) => finding.id === this.session.activeFindingId());
    this.selectFinding(findings[(index + 1) % findings.length]);
  }

  previousFinding(): void {
    const findings = this.findings();
    if (!findings.length) return;

    const index = findings.findIndex((finding) => finding.id === this.session.activeFindingId());
    this.selectFinding(findings[(index <= 0 ? findings.length : index) - 1]);
  }

  goToSubmit(): void {
    if (this.bridge.scrollToSubmit()) this.collapsed.set(true);
  }

  scopeLabel(scope: HealthReviewFinding['scope']): string {
    if (scope === 'master-data') return 'Master data';
    if (scope === 'event-or-master-data') return 'Event or master data';
    if (scope === 'traceability') return 'Traceability';
    return 'Event data';
  }

  private syncFromUrl(openDialog = false): void {
    const tree = this.router.parseUrl(this.router.url);
    const query = tree.queryParams;

    if (query['mode'] !== 'health-review' || !query['uid']) {
      this.loadSequence += 1;
      this.bridge.disconnect();
      this.session.stop();
      return;
    }

    if (openDialog) this.collapsed.set(false);

    const requestId = ++this.loadSequence;
    const eventUid = String(query['uid']);
    const requestedReturnTo = String(query['returnTo'] || '');
    const returnTo = requestedReturnTo || this.defaultHealthRoute();

    this.bridge.disconnect();
    this.session.stop();
    this.eventHealthService
      .getEventHealthByUrn(eventUid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (requestId !== this.loadSequence) return;
          const review = mapHealthReview(response, returnTo, this.customerConfig.overrides.eventHealth);
          this.session.start(review.context, review.assessment);
          this.bridge.connect(review.assessment);
        },
        error: () => {
          if (requestId !== this.loadSequence) return;
          this.bridge.disconnect();
          this.session.stop();
          this.snackBar.open('The latest event health could not be loaded. Reopen health review to retry.', 'Close', { duration: 5000 });
        },
      });
  }

  private defaultHealthRoute(): string {
    return this.router.url.startsWith('/wild-harvest/')
      ? '/wild-harvest/tools/traceability/event-health'
      : '/aquaculture/traceability/event-health';
  }
}
